'use client'
import { useActionState, useEffect, useState } from "react";
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { getMarketComparisonStream } from "./actions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { IAPIResponse } from "@/lib/interfaces";
import HttpStatus from "@/lib/HttpStatus";
import { Eraser, Loader2, Save } from "lucide-react";
import { ToastError } from "@/lib/sonnerUtils";
import Link from "next/link";
import LoginRedirect from "@/components/LoginRedirect";
import CartaTokens from '@/../public/images/tokens_sm.png';
import { GetTokenCost } from "@/lib/tokensHandlers";
import Image from "next/image";
import AdSterraIFrameBannerAuto from "@/components/adsterra-ui/adsterra-ifram-auto";
import AdSterraNativeBanner from "@/components/adsterra-ui/adsterra-native-banner";
import { DeepPartial } from "ai";
import { TCompareMarketData } from "@/lib/zod/schemas";
import { readStreamableValue } from "@ai-sdk/rsc";

function MarketDetectiveWrapper() {
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const [userLocation, setUserLocation] = useState<string>("global");
  const [product, setProduct] = useState<string>("");
  const [ detectTokenCost, setDetectTokenCost ] = useState<number>(0);
  const [ isStreaming, setIsStreaming ] = useState<boolean>(false);
  const [comparisonData, setComparisonData] = useState<DeepPartial<TCompareMarketData> | null>(null);
  const [ isCleared, setIsCleared ] = useState<boolean>(true);

  // state.data will hold our AI results
  const [state, formAction, isPending] = useActionState(getMarketComparisonStream, {
    status: 0,
    data: null,
    message: '',
    error: null
  });

  useEffect(() => {
    async function getDetectTokensCost() {
      try {
        const costResp = await GetTokenCost('MDTCT01');
  
        if(costResp.status != HttpStatus.OK) {
          throw new Error(costResp.error!);
        }
        
        setDetectTokenCost(costResp.data);
      } catch(error) {
        setDetectTokenCost(0);
      }
    }

    getDetectTokensCost();

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log(userTimezone);
    setUserLocation(userTimezone);
  }, []);

  useEffect(() => {
    if (state.status === 200 && state.data) {
    
      // 1. Fire your one-time action immediately!
      console.log("Form submission successful, dispatching token update event.");
      window.dispatchEvent(new Event('tokens-deducted'));
      
      // 2. Initialize the stream processing loop
      const processStream = async () => {
        setIsStreaming(true);
        setComparisonData(null); // Reset from any previous run

        try {
          for await (const delta of readStreamableValue(state.data)) {
            if (delta) {
              setComparisonData(delta);
            }
          }
          setIsCleared(false);

        } catch (err) {
          console.error("Error reading market stream:", err);
        } finally {
          setIsStreaming(false);
        }
      };

      processStream();
    }
    
  }, [ state ]);

  const handleSave = () => {
    if(!user || !user.id && !isLoading) {
      ToastError("User could not be authenticated. Please login and try again.");
      return;
    }

    // Saving steps here  
    
  }

  return (
    <>
      <div className="space-y-8">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Market Detective"}</span>
        </div>

        {/* <AdSterraIFrameBannerAuto position="top" /> */}
        <AdSterraNativeBanner />
        
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Market Detective</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Looking for a product or a service and you need a quick comparison of best offers?<br />
          Let's help you in that...
        </p>
        {/* SEARCH FORM */}
        <form action={formAction} className="flex flex-col gap-4 mb-12 bg-slate-50 p-6 rounded-xl border-2">
          <input type="hidden" name="timezone" value={Intl.DateTimeFormat().resolvedOptions().timeZone} />
          <div className="flex flex-row justify-center items-center gap-2">
            <div className="flex-1">
              <Input 
                name="query" 
                placeholder="What are you looking for? (e.g. Fiber Internet, Car Insurance)" 
                required
                className="bg-white"
                onChange={(e) => {
                  setProduct(e.currentTarget.value);
                  if(comparisonData) {
                    setComparisonData(null);
                    setIsCleared(true);
                  }
                }}
                value={product}
              />
            </div>
            
            <div className="w-full md:w-64">
              <Select name="sortBy" defaultValue="best-value">
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="best-value">Best Value</SelectItem>
                  <SelectItem value="lowest-price">Lowest Price</SelectItem>
                  <SelectItem value="quality">Best Quality</SelectItem>
                  <SelectItem value="support">Best Support</SelectItem>
                  <SelectItem value="fastest">Fastest Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button type="submit" disabled={isPending || isStreaming} 
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white transition duration-200">
            
            <div className="flex flex-row justify-center items-center gap-2">
              <div className="text-lg">
                {isPending || isStreaming ? 
                  <div className="flex justify-center items-center gap-2">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <div>Analysing Market...</div>
                  </div> 
                  : "Compare Now"}
              </div>
              <div className="flex flex-row justify-center items-center gap-1">
                <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                <span className="ml-1">{detectTokenCost} tokens</span><span>{")"}</span>
              </div>
            </div>
          </Button>
        </form>

        {/* RESULTS TABLE */}
        {
          state.status == 0 || isCleared ? 
          <div className="border rounded-lg overflow-hidden 
          bg-white shadow-sm h-60">
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <span className="text-5xl mb-4">🕵️‍♂️</span>
              <p>Your detective will show the results here.</p>
            </div>
          </div>
          : null
        }

        {
          state.status == HttpStatus.OK && !comparisonData && !isStreaming ? 
          <div className="border rounded-lg overflow-hidden 
          bg-white shadow-sm h-60">
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <span className="text-5xl mb-4">🔍</span>
              <p>We couldn't find anything matching your request. Try again.</p>
            </div>
          </div>
          : null
        }

        {state.error && <p className="text-red-500 mb-4">{state.error}</p>}

        {(comparisonData && comparisonData.vendors && comparisonData.vendors.length > 0) && (
          <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-[100px]">Vendor</TableHead>
                  <TableHead>Offer Details</TableHead>
                  <TableHead>Specifications</TableHead>
                  <TableHead className="text-right">Price/Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.vendors.map((item: any, index: number) => (
                  <TableRow key={index} className="hover:bg-slate-50/50">
                    <TableCell>
                      <div className="flex flex-col items-center gap-2">
                        <img 
                          src={item.logo} 
                          alt={item.vendor} 
                          className="w-12 h-12 object-contain rounded border p-1"
                          onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/50?text=Logo")}
                        />
                        <span className="font-bold text-xs text-center">{item.vendor}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <p className="font-semibold text-blue-700 text-xs md:text-sm mb-1">{item.offers}</p>
                      <p className="text-gray-600 text-xs md:text-sm italic">{item.details}</p>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-700 border">
                        {item.specs}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-bold text-lg text-slate-900">{item.price}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {
          state.status != 0 && state.status != HttpStatus.OK ?
          <div className="border rounded-lg overflow-hidden bg-white shadow-sm text-red-500">
            {state.error}
          </div>
          : null
        }

        {/* ACTION FOOTER */}
        <div className="flex flex-col md:flex-row gap-4 pt-10 border-t">
          <Button variant="destructive" className="flex-1" 
            onClick={() => window.location.reload()}          
          >
            <Eraser className="mr-2 w-4 h-4" /> Clear All
          </Button>
          <Button className="flex-1" 
            onClick={handleSave}
            disabled={state.data == null}
          >
            <Save className="mr-2 w-4 h-4" /> Save Result
          </Button>
        </div>
      </div>

      <AdSterraIFrameBannerAuto position="bottom" />

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  );
}

export default MarketDetectiveWrapper